import debug from "debug";
import { nanoid } from "nanoid";

const log = debug("app:ur");

/**
 * Class for handling transformation actions such as move, rotate, edit, delete, and replace.
 * @category Components
 * @subcategory UndoRedoManager
 */
export default class TransformAction {
  /**
   * Constructor for TransformAction.
   * @param {Object} experience - The main experience object.
   * @param {Object} experience.scene - The Three.js scene.
   * @param {Object} experience.eventHub - The event hub for emitting and listening to events.
   * @param {Object} experience.undoRedoVault - The undo/redo vault containing event logs and object mappings.
   */
  constructor(experience) {
    this.experience = experience;
    this.scene = this.experience.scene;
    this.eventHub = this.experience.eventHub;



    // Map of event types to handler functions

    /**
     * Map of event types to their corresponding handler methods.
     * @type {Object}
     */
    this.handlers = {
      move: this.handleMove.bind(this),
      rotate: this.handleRotate.bind(this),
      edit: this.handleEdit.bind(this),
      delete: this.handleDelete.bind(this),
      replace: this.handleReplace.bind(this), // Added replace handler
    };
  }

  /**
   * Initializes the action by determining the correct handler based on eventType.
   * @param {string} eventType - The type of the event (e.g., "move", "rotate").
   * @param {string} action - The action to perform (e.g., "undo", "redo").
   * @param {string} eventId - The unique ID of the event in the event log.
   */
  init(eventType, action, eventId) {
    try {
      log("Initializing TransformAction:", { eventType, action, eventId });

      const event = this.experience.undoRedoVault.eventLog.get(eventId);
      if (!event || !event.data) {
        log(`No event data found for eventId: ${eventId}`);
        return;
      }

      const handler = this.handlers[eventType];
      if (handler) {
        handler(action, event.data);
      } else {
        log(`Unhandled eventType: ${eventType}`);
      }
    } catch (error) {
      log("Error in TransformAction.init:", error);
    }
  }

  cleanup() {
    if (!this.eventHub) return; // Prevent errors if already cleaned up

    log("Cleaning up TransformAction...");

    // Unregister all event listeners
    Object.keys(this.handlers).forEach((eventType) => {
      this.eventHub.off(eventType, this.handlers[eventType]);
    });

    log("Unregistered event listeners for TransformAction");

    // Clear references
    this.experience = null;
    this.scene = null;
    this.eventHub = null;
    this.handlers = null;

    log("TransformAction cleanup completed.");
  }

  // Map of event types to handler functions

  /**
   * Handles move actions.
   * @param {string} action - The action to perform ("undo" or "redo").
   * @param {Object} eventData - The event data containing move details.
   */
  handleMove(action, eventData) {
    if (!eventData || !eventData.delta || !Array.isArray(eventData.objects)) {
      log("Invalid event data for move action:", eventData);
      return;
    }

    const { delta, objects } = eventData;
    const { x: deltaX, z: deltaZ } = delta;

    objects.forEach((objectId) => {
      const sceneObject = this.scene.children.find(
        (child) => child.userData.objectId === objectId
      );
      if (!sceneObject) {
        log(`Object with ID ${objectId} not found in scene.`);
        return;
      }

      sceneObject.position.x += action === "undo" ? -deltaX : deltaX;
      sceneObject.position.z += action === "undo" ? -deltaZ : deltaZ;
      sceneObject.updateMatrixWorld(true);

      log(
        `Object ${objectId} moved ${
          action === "undo" ? "backward" : "forward"
        } by delta:`,
        delta
      );
    });
    this.eventHub.emit("xmd");
  }

  /**
   * Handles rotate actions.
   * @param {string} action - The action to perform ("undo" or "redo").
   * @param {Object} eventData - The event data containing rotation details.
   */
  handleRotate(action, eventData) {
    if (
      !eventData ||
      typeof eventData.angle !== "number" ||
      !Array.isArray(eventData.objects)
    ) {
      log("Invalid event data for rotate action:", eventData);
      return;
    }

    const { angle, objects } = eventData;
    const angleInRadians = action === "undo" ? -angle : angle; // Keep angle in degrees as needed for the event

    // Get the objectIdMap from the experience
    const objectIdMap = this.experience.undoRedoVault.objectIdMap;
    const validObjects = objects
      .map((objectId) => {
        const uuid = objectIdMap.get(objectId);
        return uuid ? this.scene.getObjectByProperty("uuid", uuid) : null;
      })
      .filter(Boolean);

    if (!validObjects.length) {
      log("No valid objects found to rotate.");
      return;
    }

    // Emit the rotateObjects event
    this.eventHub.emit("rotateObjects", {
      angle: angleInRadians,
      objects: validObjects,
    });

    log(
      "rotateObjects event emitted with angle:",
      angleInRadians,
      "and objects:",
      validObjects
    );
    this.eventHub.emit("xmd");
  }

  /**
   * Handles edit actions.
   * @param {string} action - The action to perform ("undo" or "redo").
   * @param {Object} eventData - The event data containing edit details.
   */
  handleEdit(action, eventData) {
    log("eventData", eventData);
    if (
      !eventData ||
      !eventData.mode ||
      !eventData.objectId ||
      !eventData.previousState ||
      !eventData.changedState
    ) {
      log("Invalid event data for edit action:", eventData);
      return;
    }

    const isUndo = action === "undo";
    const objectId = eventData.objectId;

    const previousState = isUndo
      ? eventData.changedState
      : eventData.previousState;
    const changedState = isUndo
      ? eventData.previousState
      : eventData.changedState;

    const mode = isUndo
      ? eventData.previousState.mode
      : eventData.changedState.mode;

    // Emit the editChanged event with required data
    this.eventHub.emit("editChanged", {
      objectId,
      mode,
      previousState,
      changedState,
    });

    log(`${isUndo ? "Undo" : "Redo"} edit event emitted for object:`, objectId);
  }

  /**
   * Handles delete actions.
   * @param {string} action - The action to perform ("undo" or "redo").
   * @param {Object} eventData - The event data containing deletion details.
   */
  handleDelete(action, eventData) {
    if (!Array.isArray(eventData.figures) && !Array.isArray(eventData.others)) {
      log("Invalid event data for delete action:", eventData);
      return;
    }

    const payload = {
      figures: eventData.figures || [],
      others: eventData.others || [],
    };

    if (action === "undo") {
      this.eventHub.emit("makeParts", payload);
      log("Undo delete: Re-adding objects", payload);
    } else if (action === "redo") {
      this.eventHub.emit("deleteObjects", payload);
      log("Redo delete: Deleting objects", payload);
    }
  }

  /**
   * Handles replace actions.
   * @param {string} action - The action to perform ("undo" or "redo").
   * @param {Object} eventData - The event data containing replacement details.
   */
  handleReplace(action, eventData) {
    if (!eventData || !eventData.oldPart || !eventData.newPart) {
      log("Invalid event data for replace action:", eventData);
      return;
    }

    const { oldPart, newPart } = eventData;
    const isUndo = action === "undo";

    if (isUndo) {
      this.eventHub.emit("deleteObjects", { figures: [newPart] });
      log("Undo: Deleted newPart:", newPart);

      this.eventHub.emit("makeParts", { figures: [oldPart] });
      log("Undo: Recreated oldPart with original objectId:", oldPart);
    } else {
      this.eventHub.emit("deleteObjects", { figures: [oldPart] });
      log("Redo: Deleted oldPart:", oldPart);

      this.eventHub.emit("makeParts", { figures: [newPart] });
      log("Redo: Recreated newPart with original objectId:", newPart);
    }
  }
}
